import CenterFocusStrongIcon from "@mui/icons-material/CenterFocusStrong";
import CropFreeIcon from "@mui/icons-material/CropFree";
import { IconButton } from "@mui/material";

import { ThreeDPanelState, useVizDispatch } from "@/shared/state/visualization";
import { setThreeDPanelConfig } from "@/shared/state/visualization/actions";
import * as schema from "@/shared/state/visualization/schema/v1/three-d";

import { BabylonManager } from "./BabylonManager";
import { computeToggledCameraSpec } from "./computeToggledCameraSpec";
import { resolveFallbackEntity } from "./resolveFallbackEntity";

interface ModeToggleButtonProps {
  panelId: string;
  manager: BabylonManager | null;
  state: ThreeDPanelState;
}

/**
 * Header control that flips the active camera between orbit and
 * third-person modes. The orbit→thirdPerson direction reads the live
 * Babylon camera through `manager.getCameraSnapshot()` to seed the new
 * spec, preserving framing across the flip; the thirdPerson→orbit
 * direction discards the third-person framing and relies on orbit
 * construction defaults.
 *
 * Renders nothing when the panel has no camera or no entities: the
 * create-panel reducer seeds both, so this is type-level defense rather
 * than a user-reachable path.
 */
export function ModeToggleButton({
  panelId,
  manager,
  state,
}: ModeToggleButtonProps) {
  const dispatch = useVizDispatch();
  const currentSpec = state.config?.camera;
  const fallbackEntity = resolveFallbackEntity(state.data.entities);
  if (!currentSpec || !fallbackEntity) {
    return null;
  }
  const isThirdPerson = currentSpec.type === schema.CAMERA_TYPE.thirdPerson;
  // Disabled until the BabylonManager has built its initial camera; the
  // orbit→thirdPerson capture has nothing to read otherwise.
  const disabled = manager === null;
  const title = isThirdPerson
    ? "Switch to tracking mode"
    : "Switch to free camera mode";

  const onClick = () => {
    if (!manager) {
      return;
    }
    const snapshot = manager.getCameraSnapshot();
    if (!snapshot) {
      return;
    }
    const next = computeToggledCameraSpec({
      current: currentSpec,
      snapshot,
      fallbackEntity,
    });
    dispatch(setThreeDPanelConfig(panelId, { camera: next }));
  };

  return (
    <IconButton
      aria-label="Toggle camera mode"
      disabled={disabled}
      onClick={onClick}
      size="small"
      title={title}
    >
      {isThirdPerson ? (
        <CropFreeIcon color="primary" fontSize="small" />
      ) : (
        <CenterFocusStrongIcon color="inherit" fontSize="small" />
      )}
    </IconButton>
  );
}
